import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const DeliveryChallanPDF = {
  generate: async (rowData, logoUrl, companyInfo) => {

    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const margin = 15;
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      let yPos = margin;

      // ========== 1. Header ==========
      if (logoUrl) {
        await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            // Logo size adjusted
            doc.addImage(img, 'PNG', margin, yPos, 35, 18);
            resolve();
          };
          img.onerror = resolve;
          img.src = logoUrl;
        });
      }

      // Company info - original spacing
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(
        companyInfo.name || 'Company Name',
        pageWidth - margin,
        yPos + 5,
        { align: 'right' }
      );

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);

      doc.text(
        companyInfo.registered_Office || '',
        pageWidth - margin,
        yPos + 10,
        { align: 'right', maxWidth: 80 }
      );

      doc.text(
        `Call: ${companyInfo.phone || ''}`,
        pageWidth - margin,
        yPos + 20,
        { align: 'right' }
      );

      const emailWebsiteText = `E.: ${companyInfo.email || 'N/A'} | W.: ${companyInfo.Website || 'N/A'}`;

      doc.text(
        emailWebsiteText,
        pageWidth - margin,
        yPos + 25,
        { align: 'right' }
      );

      yPos += 32;

      // ========== 2. Title ==========
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(
        'DELIVERY CHALLAN',
        pageWidth / 2,
        yPos,
        { align: 'center' }
      );

      yPos += 5;

      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // ========== 3. DC Info Row ==========
      doc.setFontSize(11);

      // PO Number and PO Date - with proper spacing
      doc.setFont('helvetica', 'bold');
      doc.text('PO Number:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(rowData.purchaseOrder?.poNumber || 'N/A', margin + 25, yPos);

      doc.setFont('helvetica', 'bold');
      doc.text('PO Date:', margin + 100, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(
        rowData.purchaseOrder?.poDate
          ? new Date(rowData.purchaseOrder.poDate).toLocaleDateString('en-GB')
          : '',
        margin + 120,
        yPos
      );

      // DC Number and DC Date - increased spacing from BILLED TO
      yPos += 8; // Increased from 8 to create more space below

      doc.setFont('helvetica', 'bold');
      doc.text('DC Number:', margin, yPos);
      doc.setFont('helvetica', 'normal');
      const dcNumber = rowData.dcNumber || '';
      const dcSplit = doc.splitTextToSize(dcNumber, 60);
      doc.text(dcSplit, margin + 25, yPos);

      doc.setFont('helvetica', 'bold');
      doc.text('DC Date:', margin + 100, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(
        rowData.dcDate
          ? new Date(rowData.dcDate).toLocaleDateString('en-GB')
          : '',
        margin + 120,
        yPos
      );

      yPos += 6;
      doc.setFont('helvetica', 'bold');
      doc.text('Dispatch Through:', margin + 100, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(rowData.dispatchThrough || '', margin + 140, yPos);

      // More space before BILLED TO section
      yPos += 15; // Increased from 12 for more spacing

      // ========== 4. Billing & Shipping - Fixed Layout ==========
      doc.setFontSize(10);
      const boxWidth = 85;
      const boxPadding = 3;
      const lineHeight = 4.5;

      // Calculate billing address content
      const addressRaw = rowData.purchaseOrder?.billingAddress?.address || '';
      const addressLines = [];
      const maxCharsPerLine = 35;

      for (let i = 0; i < 4; i++) {
        const line = addressRaw.slice(i * maxCharsPerLine, (i + 1) * maxCharsPerLine);
        if (line) addressLines.push(line);
      }

      const billingLines = [
        rowData.customer?.name || '',
        ...addressLines,
        `${rowData.purchaseOrder?.billingAddress?.city || ''}, ${rowData.purchaseOrder?.billingAddress?.state || ''} - ${rowData.purchaseOrder?.billingAddress?.pincode || ''}`
      ];

      // Add GST/PAN info only if available
      if (rowData.customer?.gst) {
        billingLines.push(`GST No: ${rowData.customer.gst}`);
      }
      if (rowData.customer?.pan_no) {
        billingLines.push(`PAN No: ${rowData.customer.pan_no}`);
      }
      if (rowData.purchaseOrder?.billingAddress?.state_code) {
        billingLines.push(`State Code: ${rowData.purchaseOrder.billingAddress.state_code}`);
      }

      // Calculate shipping address content
      const rawAddress = rowData.purchaseOrder?.dispatchAddress?.address || '';
      const addressLines1 = doc.splitTextToSize(rawAddress, 80);

      let shipping = [
        rowData.customer?.name || '',
        ...addressLines1.slice(0, 3),
        `${rowData.purchaseOrder?.dispatchAddress?.city || ''}, ${rowData.purchaseOrder?.dispatchAddress?.state || ''} - ${rowData.purchaseOrder?.dispatchAddress?.pincode || ''}`
      ];

      // Add GST/PAN for shipping if same as billing
      if (rowData.customer?.gst) {
        shipping.push(`GST No: ${rowData.customer.gst}`);
        if (rowData.customer?.pan_no) {
          shipping.push(`PAN No: ${rowData.customer.pan_no}`);
        }
        if (rowData.purchaseOrder?.dispatchAddress?.state_code) {
          shipping.push(`State Code: ${rowData.purchaseOrder.dispatchAddress.state_code}`);
        }
      }

      // Calculate max height needed
      const maxLines = Math.max(billingLines.length, shipping.length);
      const sectionHeight = maxLines * lineHeight + 10;

      // Draw BILLED TO box with proper spacing from DC Date
      doc.setFont('helvetica', 'bold');
      doc.text('BILLED TO:', margin, yPos);
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, yPos + 3, boxWidth, sectionHeight, 'F'); // Moved down by 3mm
      doc.setFont('helvetica', 'normal');

      // Draw billing lines
      billingLines.forEach((line, i) => {
        const y = yPos + 8 + (i * lineHeight); // Start lower in the box

        if (line.includes("GST No:") || line.includes("PAN No:") || line.includes("State Code:")) {
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setFont('helvetica', 'normal');
        }

        doc.text(line, margin + boxPadding, y);
      });

      // Draw SHIPPED TO box
      doc.setFont('helvetica', 'bold');
      doc.text('SHIPPED TO:', margin + 95, yPos);
      doc.setFillColor(245, 245, 245);
      doc.rect(margin + 95, yPos + 3, boxWidth, sectionHeight, 'F');
      doc.setFont('helvetica', 'normal');

      // Draw shipping lines
      shipping.forEach((line, i) => {
        const y = yPos + 8 + (i * lineHeight);

        if (line.includes("GST No:") || line.includes("PAN No:") || line.includes("State Code:")) {
          doc.setFont('helvetica', 'bold');
        } else {
          doc.setFont('helvetica', 'normal');
        }

        doc.text(line, margin + 98, y);
      });

      yPos += sectionHeight + 8;

      // ========== 5. Items Table ==========
      const columns = [
        { header: 'S No.', dataKey: 'serialNumber', cellWidth: 12 },
        { header: 'HSN Code', dataKey: 'hsnCode', cellWidth: 25 },
        { header: 'Description', dataKey: 'description', cellWidth: 55 }, // Reduced from 60
        { header: 'Qty', dataKey: 'quantity', cellWidth: 15 },
        { header: 'Unit', dataKey: 'uom', cellWidth: 15 },
        { header: 'Remark', dataKey: 'remark', cellWidth: 45 } // Increased for remark space
      ];

      const tableData = (rowData.items || []).map((item, index) => ({
        serialNumber: item.purchaseOrderItem?.serialNumber || index + 1,
        hsnCode: item.purchaseOrderItem?.hsnCode || '-',
        description: doc.splitTextToSize(
          `${item.purchaseOrderItem?.item?.name || ''} ${item.purchaseOrderItem?.item?.description || ''}`,
          50 // Reduced width for description
        ).slice(0, 2).join('\n'),
        quantity: item.quantity || 0,
        uom: item.uom || item.purchaseOrderItem?.uom || '-',
        remark: doc.splitTextToSize(
          item.remark || item.purchaseOrderItem?.remark || '-',
          40 // Increased width for remark
        ).join('\n') // Allow multiple lines for remark
      }));

      // Table with reserved footer space
      doc.autoTable({
        startY: yPos,
        head: [columns.map(col => col.header)],
        body: tableData.map(row => columns.map(col => row[col.dataKey])),
        margin: { left: margin, right: margin, bottom: 45 }, // Increased bottom margin to accommodate signature section
        styles: {
          fontSize: 8,
          cellPadding: 1.2,
          minCellHeight: 6,
          lineWidth: 0.1,
          overflow: 'linebreak',
          lineColor: [180, 180, 180],
        },
        headStyles: {
          fillColor: [51, 122, 183],
          textColor: 255,
          fontStyle: 'bold',
          cellPadding: 1.5,
          minCellHeight: 7
        },
        bodyStyles: {
          cellPadding: 1.2
        },
        rowPageBreak: 'avoid'
      });

      yPos = doc.lastAutoTable.finalY + 15; // Increased spacing from table to signature section

      // ========== 6. Signature Section - PERFECT ALIGNMENT ==========
      // Check if we need a new page for signature section
      const signatureSectionHeight = 25;
      if (yPos + signatureSectionHeight > pageHeight - 25) { // 25mm is footer height
        doc.addPage();
        yPos = margin;
      }

      // Add space before signature section
      yPos += 5; // Add space before signature section

      // Set text positions
      const signatureLineY = yPos + 10; // Y position for horizontal lines
      const lineLength = 50; // Length of signature line

      // "For" text - positioned 5mm above the line
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');

      // Left side: For Gmmco Ltd. - LEFT ALIGNED
      doc.text('For Gmmco Ltd.', margin, signatureLineY - 5);

      // Right side: For SSPIPL - PERFECTLY ALIGNED ABOVE LINE
      // Calculate exact position for right side text
      const rightTextX = pageWidth - margin - lineLength; // Start of the right line
      doc.text('For SSPIPL', rightTextX, signatureLineY - 5);

      // Draw horizontal lines
      doc.line(margin, signatureLineY, margin + lineLength, signatureLineY);
      doc.line(pageWidth - margin - lineLength, signatureLineY, pageWidth - margin, signatureLineY);

      // Signature labels - positioned 5mm below the line
      const signatureLabelY = signatureLineY + 7;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      // Left side: Signature & Stamp - CENTERED UNDER LINE
      const leftLabelX = margin + (lineLength / 2);
      doc.text('Signature & Stamp', leftLabelX, signatureLabelY, { align: 'center' });

      // Right side: Authorized Signature - PERFECTLY CENTERED UNDER LINE
      const rightLabelX = pageWidth - margin - (lineLength / 2);
      doc.text('Authorized Signature', rightLabelX, signatureLabelY, { align: 'center' });

      // ========== 7. Footer ==========
      const totalPages = doc.internal.getNumberOfPages();

      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        const pageHeight = doc.internal.pageSize.height;

        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.line(margin, pageHeight - 18, pageWidth - margin, pageHeight - 18);
        doc.setFont('helvetica', 'bold');
        doc.text(companyInfo.name || '', pageWidth / 2, pageHeight - 12, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 12, { align: 'right' });
      }

      // Save PDF
      doc.save(`DC_${rowData.dcNumber || 'document'}.pdf`);
    } catch (err) {
      console.error('PDF Generation Error:', err);
      const doc = new jsPDF();
      doc.text('Error generating delivery challan PDF', 10, 10);
      doc.text(err.message || '', 10, 20);
      doc.save('error.pdf');
    }
  }
};

export default DeliveryChallanPDF;